import {Component, computed, OnInit, signal} from '@angular/core';
import {ControlsOf, FormControl, FormGroup} from '@ngneat/reactive-forms';
import {Leave, LeaveType} from '../../classes/leave';
import {CodaltComponent} from '../../codalt.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ReactiveFormsModule, Validators} from '@angular/forms';
import {LeaveService} from '../../services/leave.service';
import {Settings} from '../../settings.class';
import {Utils} from '../../utils.class';
import {UserService} from '../../services/user/user.service';
import {User} from '../../classes/user.class';
import {DatePipe, formatDate} from '@angular/common';
import {debounceTime, startWith} from 'rxjs/operators';
import {MatFormField, MatHint, MatInput, MatSuffix} from '@angular/material/input';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {MatCheckbox} from '@angular/material/checkbox';
import {NgNotFoundTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatTooltip} from '@angular/material/tooltip';
import {MinutesPipe} from '../../pipes/minutes.pipe';
import {MatButton} from '@angular/material/button';
import {DatetimeInputComponent} from '../../services/datetime-input/datetime-input.component';
import {Registration} from '../../classes/registration';
import {Routenames} from '../../classes/route-name.enum';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Roles} from '../../services/auth/roles';
import {HasRolePipe} from '../../pipes/has-role.pipe';
import {DatetimeBeforePipe} from '../../pipes/datetime-before.pipe';
import {ByIdPipe} from '../../by-id.pipe';
import {
    BreadcrumbsComponent,
    DataListComponent,
    DataRowComponent,
    EmptyStateComponent,
    FieldComponent,
    FilterChipsComponent,
    FilterOption,
    IconTileComponent,
    InfoBannerComponent,
    PageComponent,
    SectionComponent,
    StickySaveBarComponent
} from '../../shared/ui';
import {hoursLabel, leaveTypeStyle, WEEKDAYS} from '../leave-type-style';

/** Time presets of the design; "Hele dag" is the default leave time (Settings.DEFAULT_START + DEFAULT_DURATION_SLEEP). */
const PRESETS = [
    {value: 'day', label: 'Hele dag', from: Settings.DEFAULT_START, till: Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP},
    {value: 'morning', label: 'Ochtend', from: Settings.DEFAULT_START, till: 13},
    {value: 'afternoon', label: 'Middag', from: 13, till: Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP},
];

@Component({
    selector: 'app-leave-edit',
    templateUrl: './leave-edit.component.html',
    styleUrls: ['./leave-edit.component.scss'],
    imports: [
        MatFormField,
        MatHint,
        MatSuffix,
        ReactiveFormsModule,
        FieldErrorComponent,
        MatCheckbox,
        NgSelectComponent,
        NgNotFoundTemplateDirective,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        MatInput,
        MatButton,
        MatTooltip,
        MinutesPipe,
        DatePipe,
        RouterLink,
        DatetimeInputComponent,
        ByIdPipe,
        HasRolePipe,
        PageComponent,
        BreadcrumbsComponent,
        SectionComponent,
        FieldComponent,
        IconTileComponent,
        FilterChipsComponent,
        InfoBannerComponent,
        DataListComponent,
        DataRowComponent,
        EmptyStateComponent,
        StickySaveBarComponent,
    ]
})
export class LeaveEditComponent extends CodaltComponent implements OnInit {
    readonly form = signal<FormGroup<ControlsOf<FormGroupLeave>>>(null);
    readonly leave = signal<Leave>(null);
    readonly leaveTypes = signal<LeaveType[]>(null);
    readonly previewRealisations = signal<Registration[]>(null);
    readonly users = signal<User[]>(null);
    readonly registrations = signal<Registration[]>(null);
    readonly today = Utils.setTime(new Date(), 0, 0);

    minDate: Date;
    maxDate: Date;
    saving = false;
    saved = false;

    duration = 0;
    highlight = null;

    now = new Date();
    canSaveLeave: boolean = true;

    // Presentation helpers for the design (tiles, weekday buttons, presets, totals)
    readonly leaveTypeStyle = leaveTypeStyle;
    readonly weekdays = WEEKDAYS;
    readonly presetOptions: FilterOption[] = PRESETS.map(({value, label}) => ({value, label}));
    readonly previewMinutes = computed(() => (this.previewRealisations() ?? [])
        .reduce((total, preview) => total + Math.max(0, (preview.till.getTime() - preview.from.getTime()) / 60000), 0));
    readonly hoursLabel = hoursLabel;

    constructor(
        private activatedRoute: ActivatedRoute,
        private leaveService: LeaveService,
        private router: Router,
        private userService: UserService,
    ) {
        super();
    }

    ngOnInit(): void {
        if (AuthorisationService.hasRole(Roles.Management)) {
            this.subscriptions.add(
                this.userService.getList().subscribe(users => {
                    this.users.set(users.filter(user => {
                        const now = new Date();
                        return user.employment_end === null || new Date(user.employment_end) < now;
                    }));
                })
            );
        }

        this.subscriptions.add(
            this.leaveService.getLeaveTypes().subscribe(leaveTypes => {
                if (AuthorisationService.hasRole(Roles.Management)) {
                    this.leaveTypes.set(leaveTypes);
                } else {
                    this.leaveTypes.set(leaveTypes.filter(leaveType => leaveType.allowed_user));
                }
            })
        );

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { id }) => {
                this.crumbs = [
                    {name: 'Verlof', route: Routenames.leave},
                    {name: `Verlof : ${params.id === 'new' ? 'nieuw' : params.id}`, route: `${Routenames.leave}/${params.id}`}
                ];
                if (params.id === 'new') {
                    const leave = new Leave();
                    leave.users = [this.LocalStorage.user];
                    this.createForm(leave);
                } else {
                    this.subscriptions.add(
                        this.leaveService.getLeave(params.id).subscribe(leave => {
                            this.createForm(leave);
                        })
                    );
                }
            })
        );
    }

    editHours(date: Date, userId: number) {
        this.router.navigate([
            `/hours/${formatDate(date, 'yyyy-MM-dd', 'nl')}/${userId}`,
            {backTo: window.location.pathname}
        ]);
    }

    createForm(leave: Leave) {
        this.leave.set(leave);

        const beginDate = new Date();
        Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);
        const endDate = new Date(beginDate);
        Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);

        if (!leave.id || !Utils.isPast(leave.begindate)) {
            this.minDate = new Date(beginDate);
            this.minDate.setDate(this.minDate.getDate() - 100);
            this.minDate = new Date(Math.max(this.today.getTime(), this.minDate.getTime()));
        } else {
            this.minDate = new Date(leave.begindate);
        }

        const begintime = new Date();
        if (!leave.begintime) {
            Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);
        } else {
            const time = leave.begintime.split(':');
            Utils.setTime(begintime, +time[0], +time[1], 0);
        }
        const endtime = new Date();
        if (!leave.endtime) {
            Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
        } else {
            const time = leave.endtime.split(':');
            Utils.setTime(endtime, +time[0], +time[1], 0);
        }

        this.form.set(new FormGroup<ControlsOf<FormGroupLeave>>({
                id: new FormControl(leave.id),
                leave_type: new FormControl(leave.leave_type, Validators.required),
                comment: new FormControl(leave.comment),
                public_comment: new FormControl(leave.public_comment),
                user_id: new FormControl(leave.user_id),
                users: new FormControl(leave.users?.map(u => u.id)),
                common: new FormControl(leave.common ?? false),
                begindate: new FormControl(leave.begindate ? new Date(leave.begindate) : beginDate, Validators.required),
                enddate: new FormControl(leave.enddate ? new Date(leave.enddate) : endDate, Validators.required),
                begintime: new FormControl(begintime),
                endtime: new FormControl(endtime),
                interval_week: new FormControl(leave.interval_week, Validators.max(52)),
                repeat_monday: new FormControl(leave.repeat_monday),
                repeat_tuesday: new FormControl(leave.repeat_tuesday),
                repeat_wednesday: new FormControl(leave.repeat_wednesday),
                repeat_thursday: new FormControl(leave.repeat_thursday),
                repeat_friday: new FormControl(leave.repeat_friday),
                repeat_saturday: new FormControl(leave.repeat_saturday),
                repeat_sunday: new FormControl(leave.repeat_sunday),
                updated_at: new FormControl(leave.updated_at),
                interval: new FormControl((leave.interval_week > 0))
            })
        );
        if (leave.id) {
            this.form()?.controls.common.disable();
        }

        this.subscriptions.add(this.form()?.controls.common.valueChanges.subscribe(common => {
            if (common) {
                this.form()?.controls.users.disable();
                this.form()?.controls.users.reset();
            } else {
                this.form()?.controls.users.enable();
            }
        }));
        if (leave.id) {
            this.subscriptions.add(
                this.leaveService.getLeaveRealisations(leave.id).subscribe(realistations => {
                    this.registrations.set(realistations);
                })
            );
        }
        this.subscriptions.add(
            this.form()?.controls.interval.valueChanges.subscribe(interval => {
                if (this.form()?.value.interval !== interval) {
                    this.form()?.patchValue({
                        repeat_monday: interval,
                        repeat_tuesday: interval,
                        repeat_wednesday: interval,
                        repeat_thursday: interval,
                        repeat_friday: interval
                    });
                    if (interval) {
                        this.form()?.controls.interval_week.setValue(1);
                    } else {
                        this.form()?.controls.interval_week.setValue(0);
                    }
                }
            })
        );

        this.subscriptions.add(
            this.form()?.controls.begindate.valueChanges.subscribe(begindate => {
                if (!this.form()?.controls.interval.value) {
                    if (begindate.getHours() !== 0) {
                        const beginDate = new Date(begindate);
                        Utils.setTime(beginDate, 0, 0, 0);
                        this.form()?.controls.begindate.setValue(beginDate);
                    }
                }
                if ((this.form()?.value.enddate?.getTime() < begindate.getTime()) || !this.form()?.controls.interval.value) {
                    const endDate = new Date(begindate);
                    Utils.setTime(endDate, 23, 59, 59);
                    this.form()?.controls.enddate.setValue(endDate);
                }

                this.maxDate = new Date(begindate);
                if (this.form()?.getRawValue().common) {
                    this.maxDate.setDate(this.maxDate.getDate() + 28);
                } else {
                    this.maxDate.setFullYear(this.maxDate.getFullYear() + 3);
                }
            })
        );

        this.subscriptions.add(
            this.form()?.valueChanges.pipe(debounceTime(500), startWith()).subscribe(() => {
                this.duration = Utils.minuteDuration(this.form()?.value.endtime, this.form()?.value.begintime);
                this.generateRealisations();
            })
        );

        if (this.leave().id !== undefined) {
            const datetimeBefore = new DatetimeBeforePipe();
            if ((!datetimeBefore.transform(this.now, new Date(this.leave().enddate))) || (!AuthorisationService.hasRole(Roles.Management) && leave.approved_by && leave.approved_at)) {
                this.form().disable();
                this.canSaveLeave = false;
                this.duration = Utils.minuteDuration(this.form()?.controls.endtime.value, this.form()?.controls.begintime.value);

            }
        }
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form()?.valid && this.canSaveLeave) {
            const leave = new Leave();
            Object.assign(leave, this.form()?.value);
            leave.begintime = formatDate(leave.begintime, 'HH:mm', 'nl');
            leave.endtime = formatDate(leave.endtime, 'HH:mm', 'nl');

            this.subscriptions.add(
                this.leaveService.saveLeave(leave).subscribe(() => {
                    this.router.navigate([Routenames.leave]);
                })
            );
        }
    }

    /** Leave type tile: sets the existing leave_type control like the former select did. */
    pickLeaveType(key: string) {
        const control = this.form()?.controls.leave_type;
        if (control && control.enabled && control.value !== key) {
            control.setValue(key);
            control.markAsDirty();
        }
    }

    /** Weekday button: toggles the existing repeat_<day> checkbox control. */
    toggleWeekday(key: typeof WEEKDAYS[number]['key']) {
        const control = this.weekdayControl(key);
        if (control && control.enabled) {
            control.setValue(!control.value);
            control.markAsDirty();
        }
    }

    weekdayControl(key: typeof WEEKDAYS[number]['key']) {
        return this.form()?.controls[`repeat_${key}` as const];
    }

    /** "Hele dag", "Ochtend", "Middag": fills the existing begintime/endtime controls. */
    applyPreset(value: string) {
        const preset = PRESETS.find(p => p.value === value);
        const form = this.form();
        if (!preset || !form || form.disabled) {
            return;
        }
        form.controls.begintime.setValue(Utils.setTime(new Date(form.controls.begintime.value ?? new Date()), preset.from, 0, 0));
        form.controls.endtime.setValue(Utils.setTime(new Date(form.controls.endtime.value ?? new Date()), preset.till, 0, 0));
        form.markAsDirty();
    }

    activePreset(begintime: Date, endtime: Date): string | null {
        const minutes = (date: Date) => date ? date.getHours() * 60 + date.getMinutes() : null;
        return PRESETS.find(p => minutes(begintime) === p.from * 60 && minutes(endtime) === p.till * 60)?.value ?? null;
    }

    /** Duration per day for "Duur per dag", from the raw control values so it also shows on a read-only leave. */
    dayMinutes(): number {
        const controls = this.form()?.controls;
        return controls ? Utils.minuteDuration(controls.endtime.value, controls.begintime.value) : 0;
    }

    backToOverview() {
        this.router.navigate([Routenames.leave]);
    }

    mouseOverDate(date: Date) {
        this.highlight = formatDate(date, 'yyyy-MM-dd', 'nl');
    }

    mouseOverUser(userId: number) {
        this.highlight = userId;
    }

    generateRealisations() {
        let formBegindate: string | number | Date;
        let formEnddate: string | number | Date;
        let formBegintime: Date;
        let formEndtime: Date;
        let formInterval: boolean;
        let formIntervalWeek: number;

        if (!this.canSaveLeave) {
            formBegindate = this.form()?.controls.begindate.value;
            formEnddate = this.form()?.controls.enddate.value;
            formBegintime = this.form()?.controls.begintime.value;
            formEndtime = this.form()?.controls.endtime.value;
            formInterval = this.form()?.controls.interval.value;
            formIntervalWeek = this.form()?.controls.interval_week.value;
        } else {
            formBegindate = this.form()?.value.begindate;
            formEnddate = this.form()?.value.enddate;
            formBegintime = this.form()?.value.begintime;
            formEndtime = this.form()?.value.endtime;
            formInterval = this.form()?.value.interval;
            formIntervalWeek = this.form()?.value.interval_week;
        }

        const realisations = [];
        const loopDate = new Date(formBegindate);
        const lastEndDate = new Date(formEnddate);
        const firstWeek = +formatDate(loopDate, 'w', 'nl');
        const startTime = new Date(formBegindate);
        Utils.setTime(startTime, formBegintime.getHours(), formBegintime.getMinutes());
        const endTime = new Date(formEnddate);
        Utils.setTime(endTime, formEndtime.getHours(), formEndtime.getMinutes());

        const loopEndDate = new Date(formEnddate);
        Utils.setTime(loopEndDate, 23, 59, 59);

        while (loopDate.getTime() < loopEndDate.getTime()) {

            let generateThisDay = !formInterval;
            if (formIntervalWeek > 0) {
                const week = +formatDate(loopDate, 'w', 'nl');
                const thisWeek = ((week - firstWeek) % formIntervalWeek) === 0;
                const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();
                const thisDay = this.form()?.value[`repeat_${weekDayName}`];
                generateThisDay = thisWeek && thisDay;
            }

            if (generateThisDay) {
                const beginDate = new Date(loopDate);
                if (beginDate.getTime() === Utils.getTimeOrNull(formBegindate) && beginDate.getTime() > startTime.getTime()) {
                    Utils.setTime(beginDate, formBegindate.getHours(), formBegindate.getMinutes(), 0);
                } else {
                    Utils.setTime(beginDate, startTime.getHours(), startTime.getMinutes(), 0);
                }

                const endDate = new Date(loopDate);
                Utils.setTime(endDate, formEnddate.getHours(), formEnddate.getMinutes(), 0);

                if (endDate.getTime() !== lastEndDate.getTime() || lastEndDate.getTime() > endTime.getTime()) {
                    Utils.setTime(endDate, endTime.getHours(), endTime.getMinutes(), 0);
                }

                const realisation = new Registration();
                realisation.from = beginDate;
                realisation.till = endDate;
                realisations.push(realisation);
            }
            loopDate.setDate(loopDate.getDate() + 1);
        }
        this.previewRealisations.set(realisations);
    }
}

export interface FormGroupLeave extends Omit<Leave, 'begindate' | 'enddate' | 'updated_at' | 'users' | 'begintime' | 'endtime' | 'leave_type_class'> {
    begindate: FormControl<Date>;
    enddate: FormControl<Date>;
    begintime: FormControl<Date>;
    endtime: FormControl<Date>;
    interval: boolean;
    users: FormControl<number[]>;
    updated_at: FormControl<Date>;
}
